import { useState } from 'react';
import { useAuditData } from '@/hooks/useAuditData';
import { useAuditHistory, useSiteHistoryChartData } from '@/hooks/useAuditHistory';
import { getScoreColor } from '@/lib/utils';
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  BarChart3,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { TrendData } from '@/types/audit';

type MetricView = 'scores' | 'vitals';

export function Trends() {
  const { data, loading: dataLoading } = useAuditData();
  const { history, loading: historyLoading } = useAuditHistory();
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [metricView, setMetricView] = useState<MetricView>('scores');

  const loading = dataLoading || historyLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">No audit data available</p>
      </div>
    );
  }

  if (!history || history.sites.length === 0) {
    return (
      <div className="max-w-4xl mx-auto py-16 text-center">
        <BarChart3 className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
        <h1 className="text-2xl font-light mb-2">No Historical Data Yet</h1>
        <p className="text-muted-foreground mb-6">
          Run multiple audits to see trends over time. Historical data is
          automatically collected each time you run the audit CLI.
        </p>
        <div className="bg-secondary/30 rounded-lg p-4 inline-block text-left">
          <p className="text-sm text-muted-foreground mb-2">Run audits with:</p>
          <code className="text-sm font-mono bg-secondary px-2 py-1 rounded">
            npm run audit
          </code>
        </div>
      </div>
    );
  }

  const activeSite = selectedSite || (history.sites[0]?.siteId ?? null);

  return (
    <div className="max-w-6xl mx-auto space-y-12 py-8">
      {/* Header */}
      <header className="space-y-2">
        <p className="text-sm text-muted-foreground uppercase tracking-wider">
          Trends
        </p>
        <h1 className="text-4xl font-light">Historical Analysis</h1>
        <p className="text-muted-foreground">
          Track performance changes over {history.sites.reduce((max, s) => Math.max(max, s.snapshots.length), 0)} audits
        </p>
      </header>

      {/* Site Selector */}
      <section className="flex flex-wrap gap-2">
        {history.sites.map((site) => (
          <button
            key={site.siteId}
            onClick={() => setSelectedSite(site.siteId)}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              activeSite === site.siteId
                ? 'bg-foreground text-background'
                : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            {site.siteName}
            <span className="ml-2 text-xs opacity-60">
              ({site.snapshots.length} audits)
            </span>
          </button>
        ))}
      </section>

      {activeSite && (
        <SiteTrendView
          siteId={activeSite}
          metricView={metricView}
          setMetricView={setMetricView}
        />
      )}
    </div>
  );
}

function SiteTrendView({
  siteId,
  metricView,
  setMetricView,
}: {
  siteId: string;
  metricView: MetricView;
  setMetricView: (view: MetricView) => void;
}) {
  const { chartData } = useSiteHistoryChartData(siteId);
  const { getComparison, getSiteHistory } = useAuditHistory();

  const comparison = getComparison(siteId);
  const siteHistory = getSiteHistory(siteId);

  if (!siteHistory || chartData.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No historical data for this site
      </div>
    );
  }

  const latestSnapshot = siteHistory.snapshots[siteHistory.snapshots.length - 1];

  return (
    <div className="space-y-8">
      {/* Quick Stats */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <TrendCard
          label="Performance"
          value={latestSnapshot.performanceScore}
          trend={comparison?.performance}
          goodDirection="up"
        />
        <TrendCard
          label="SEO"
          value={latestSnapshot.seoScore}
          trend={comparison?.seo}
          goodDirection="up"
        />
        <TrendCard
          label="Accessibility"
          value={latestSnapshot.accessibilityScore}
          trend={comparison?.accessibility}
          goodDirection="up"
        />
        <TrendCard
          label="Tracking"
          value={latestSnapshot.trackingScore}
          suffix="%"
          trend={comparison?.tracking}
          goodDirection="up"
        />
        <TrendCard
          label="Critical Issues"
          value={latestSnapshot.criticalIssueCount}
          trend={comparison?.criticalIssues}
          goodDirection="down"
        />
      </section>

      {/* Metric View Toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => setMetricView('scores')}
          className={`px-3 py-1.5 rounded text-sm transition-colors ${
            metricView === 'scores'
              ? 'bg-foreground text-background'
              : 'bg-secondary hover:bg-secondary/80'
          }`}
        >
          Scores
        </button>
        <button
          onClick={() => setMetricView('vitals')}
          className={`px-3 py-1.5 rounded text-sm transition-colors ${
            metricView === 'vitals'
              ? 'bg-foreground text-background'
              : 'bg-secondary hover:bg-secondary/80'
          }`}
        >
          Core Web Vitals
        </button>
      </div>

      {/* Scores Chart */}
      {metricView === 'scores' && (
        <section className="space-y-4">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Score Trends
          </h2>
          <div className="bg-secondary/30 rounded-lg p-6">
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    tick={{ fill: '#999', fontSize: 12 }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    stroke="#666"
                    tick={{ fill: '#999', fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                    }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="performance"
                    name="Performance"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="seo"
                    name="SEO"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="accessibility"
                    name="Accessibility"
                    stroke="#a855f7"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="tracking"
                    name="Tracking"
                    stroke="#f97316"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      {/* Vitals Chart */}
      {metricView === 'vitals' && (
        <section className="space-y-4">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Core Web Vitals Trends
          </h2>
          <div className="bg-secondary/30 rounded-lg p-6">
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    tick={{ fill: '#999', fontSize: 12 }}
                  />
                  <YAxis
                    stroke="#666"
                    tick={{ fill: '#999', fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                    }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value: number, name: string) => {
                      const unit = name === 'CLS' ? '' : 's';
                      return [`${value.toFixed(2)}${unit}`, name];
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="lcp"
                    name="LCP"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="fcp"
                    name="FCP"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="ttfb"
                    name="TTFB"
                    stroke="#a855f7"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="cls"
                    name="CLS"
                    stroke="#f97316"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      {/* Audit History Table */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Audit History
        </h2>
        <div className="bg-secondary/30 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 font-medium text-muted-foreground">
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Date
                </th>
                <th className="text-center p-4 font-medium text-muted-foreground">
                  Performance
                </th>
                <th className="text-center p-4 font-medium text-muted-foreground">
                  SEO
                </th>
                <th className="text-center p-4 font-medium text-muted-foreground">
                  Accessibility
                </th>
                <th className="text-center p-4 font-medium text-muted-foreground">
                  Tracking
                </th>
                <th className="text-center p-4 font-medium text-muted-foreground">
                  Issues
                </th>
              </tr>
            </thead>
            <tbody>
              {[...siteHistory.snapshots].reverse().map((snapshot, index) => (
                <tr
                  key={snapshot.auditedAt}
                  className="border-b border-border/50 hover:bg-secondary/20"
                >
                  <td className="p-4 text-muted-foreground">
                    {new Date(snapshot.auditedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {index === 0 && (
                      <span className="ml-2 text-xs bg-foreground text-background px-1.5 py-0.5 rounded">
                        Latest
                      </span>
                    )}
                  </td>
                  <td
                    className={`text-center p-4 font-medium ${getScoreColor(
                      snapshot.performanceScore
                    )}`}
                  >
                    {snapshot.performanceScore}
                  </td>
                  <td
                    className={`text-center p-4 font-medium ${getScoreColor(
                      snapshot.seoScore
                    )}`}
                  >
                    {snapshot.seoScore}
                  </td>
                  <td
                    className={`text-center p-4 font-medium ${getScoreColor(
                      snapshot.accessibilityScore
                    )}`}
                  >
                    {snapshot.accessibilityScore}
                  </td>
                  <td
                    className={`text-center p-4 font-medium ${getScoreColor(
                      snapshot.trackingScore
                    )}`}
                  >
                    {snapshot.trackingScore}%
                  </td>
                  <td className="text-center p-4">
                    {snapshot.criticalIssueCount > 0 ? (
                      <span className="text-red-400">
                        {snapshot.criticalIssueCount} critical
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        {snapshot.recommendationCount} total
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function TrendCard({
  label,
  value,
  suffix = '',
  trend,
  goodDirection,
}: {
  label: string;
  value: number;
  suffix?: string;
  trend: TrendData | null | undefined;
  goodDirection: 'up' | 'down';
}) {
  const getTrendColor = () => {
    if (!trend || trend.direction === 'stable') return 'text-muted-foreground';
    const isGood =
      (trend.direction === 'up' && goodDirection === 'up') ||
      (trend.direction === 'down' && goodDirection === 'down');
    return isGood ? 'text-green-400' : 'text-red-400';
  };

  const TrendIcon =
    !trend || trend.direction === 'stable'
      ? Minus
      : trend.direction === 'up'
      ? TrendingUp
      : TrendingDown;

  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <span className={`text-2xl font-light ${getScoreColor(value)}`}>
          {value}
          {suffix}
        </span>
        {trend && (
          <span className={`flex items-center gap-1 text-xs ${getTrendColor()}`}>
            <TrendIcon className="h-3 w-3" />
            {trend.direction !== 'stable' && (
              <span>
                {trend.change > 0 ? '+' : ''}
                {Math.round(trend.change)}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
